/**
 * Central handler for processing and enriching different alert types
 */

import { Alert } from '../../../types/alert';

export interface AlertClickAction {
  type: 'navigate' | 'modal' | 'external' | 'none';
  target?: string; // URL path for navigate, external URL, or modal ID
  data?: Record<string, any>; // Additional data to pass
}

type AlertProcessor = (alert: Alert) => Alert & { onClickAction?: AlertClickAction };

const formatAlertTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Map of alert task types to their processing handlers
 * Each handler receives the raw alert and returns an enriched version
 */
const alertProcessors: Record<string, AlertProcessor> = {
  // Video call alerts
  alert_robot_video_start: alert => ({
    ...alert,
    notificationText: `${alert.robot} started video call at ${new Date(alert.ts).toLocaleTimeString()}`,
    severity: 'info',
    category: 'video',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, action: 'video_call' },
    },
  }),

  alert_robot_video_end: alert => ({
    ...alert,
    notificationText: `${alert.robot} ended video call at ${new Date(alert.ts).toLocaleTimeString()}`,
    severity: 'info',
    category: 'video',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot },
    },
  }),

  alert_help_info_activated: alert => {
    const robotRole = alert.log?.robotRole || alert.role || 'Robot';
    const timestamp = alert.log?.timestamp || alert.ts;
    const formattedTimestamp = formatAlertTimestamp(timestamp);
    const lastArrivedLocation = alert.log?.lastArrivedLocation || 'an unknown location';

    return {
      ...alert,
      notificationText: `${robotRole} ${alert.robot} at ${alert.museum}: User clicked 'Help' button in tour info screen (${formattedTimestamp}). Robot is near ${lastArrivedLocation}.`,
      severity: 'info',
      category: 'help',
      onClickAction: {
        type: 'navigate',
        target: '/robots',
        data: {
          robot: alert.robot,
          action: 'help_info_activated',
          lastArrivedLocation,
        },
      },
    };
  },

  // Robot failure alerts
  alert_robot_failed_door: alert => ({
    ...alert,
    notificationText: `${alert.robot} failed to go through doors at ${alert.museum}`,
    severity: 'critical',
    category: 'navigation',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, issue: 'door_failure' },
    },
  }),

  // alert_robot_failed_path to be used in the future
  ROBOT_ARRIVAL_FAILURE: alert => ({
    ...alert,
    notificationText: `${alert.robot} at ${alert.museum} navigation path blocked.`,
    severity: 'warning',
    category: 'navigation',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, issue: 'path_blocked' },
    },
  }),

  alert_robot_failed_localise: alert => ({
    ...alert,
    notificationText: `${alert.robot} failed to localise at ${alert.museum}`,
    severity: 'critical',
    category: 'navigation',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, issue: 'localisation_failure' },
    },
  }),

  alert_robot_failed_task: alert => {
    const taskName = alert.log?.task || 'task';
    return {
      ...alert,
      notificationText: `${alert.robot} failed to complete ${taskName} within time limit at ${alert.museum}`,
      severity: 'warning',
      category: 'task',
      onClickAction: {
        type: 'navigate',
        target: '/robots',
        data: { robot: alert.robot, issue: 'task_timeout', task: taskName },
      },
    };
  },

  alert_robot_failed_unknown: alert => {
    const error = alert.log?.error || alert.content || 'Unknown error';
    return {
      ...alert,
      notificationText: `${alert.robot} encountered an error: ${error}`,
      severity: 'critical',
      category: 'error',
      onClickAction: {
        type: 'navigate',
        target: '/robots',
        data: { robot: alert.robot, error },
      },
    };
  },

  // Connection alerts
  alert_robot_connection_lost: alert => ({
    ...alert,
    notificationText: `${alert.robot} has lost connection at ${alert.museum}`,
    severity: 'critical',
    category: 'connection',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, status: 'disconnected' },
    },
  }),

  alert_robot_connection_found: alert => ({
    ...alert,
    notificationText: `${alert.robot} connection restored at ${alert.museum}`,
    severity: 'info',
    category: 'connection',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, status: 'connected' },
    },
  }),

  // Battery alerts
  alert_robot_battery_low: alert => ({
    ...alert,
    notificationText: `${alert.robot} battery is low at ${alert.museum}`,
    severity: 'warning',
    category: 'battery',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot, issue: 'battery_low' },
    },
  }),
};

/**
 * Process and enrich an alert with additional metadata
 * @param alert - The raw alert data object
 * @returns The processed alert with notificationText, severity, and category
 */
export const processAlert = (alert: Alert): Alert => {
  const processor = alertProcessors[alert.task];

  if (processor) {
    return processor(alert);
  }

  // Default fallback processing for unknown alert types
  return {
    ...alert,
    notificationText: alert.content || `${alert.robot} - ${alert.task}`,
    severity: 'info',
    category: 'general',
    onClickAction: {
      type: 'navigate',
      target: '/robots',
      data: { robot: alert.robot },
    },
  };
};

/**
 * Process multiple alerts
 * @param alerts - Array of raw alert data
 * @returns Array of processed alerts
 */
export const processAlerts = (alerts: Alert[]): Alert[] => {
  return alerts.map(processAlert);
};

/**
 * Check if an alert type has a processor
 * @param taskType - The alert task type
 * @returns True if processor exists
 */
export const hasAlertProcessor = (taskType: string): boolean => {
  return taskType in alertProcessors;
};

/**
 * Register a new alert processor dynamically
 * @param taskType - The alert task type
 * @param processor - The alert processing function
 */
export const registerAlertProcessor = (taskType: string, processor: AlertProcessor): void => {
  alertProcessors[taskType] = processor;
};

/**
 * Get all registered alert types
 * @returns Array of alert task types
 */
export const getRegisteredAlertTypes = (): string[] => {
  return Object.keys(alertProcessors);
};

/**
 * Execute the onClick action for an alert
 * @param alert - The alert with onClickAction configured
 * @param navigate - React Router navigate function
 */
export const executeAlertClickAction = (
  alert: Alert,
  navigate: (to: string, options?: { state?: Record<string, any> }) => void,
): void => {
  const action = alert.onClickAction;
  if (!action || action.type === 'none') {
    return;
  }

  switch (action.type) {
    case 'navigate':
      if (action.target) {
        // Navigate with state data if provided
        navigate(action.target, { state: action.data });
      }
      break;
    case 'external':
      if (action.target) {
        window.open(action.target, '_blank', 'noopener,noreferrer');
      }
      break;
    case 'modal':
      // Modal handling can be implemented here or via callback
      console.log('Open modal:', action.target, action.data);
      break;
    default:
      break;
  }
};

/**
 * Handle alert click - combines callback and action execution
 * @param alert - The alert being clicked
 * @param navigate - React Router navigate function
 * @param onAlertClick - Optional callback to execute before action
 */
export const handleAlertClick = (
  alert: Alert,
  navigate: (to: string, options?: { state?: Record<string, any> }) => void,
  onAlertClick?: (alert: Alert) => void,
): void => {
  console.log('Executing onClick action for alert ID:', alert.id);

  // Call the optional onClick callback first
  if (onAlertClick) {
    onAlertClick(alert);
  }

  // Execute the configured onClick action
  executeAlertClickAction(alert, navigate);
  console.log('Alert clicked:', alert);
};
